<script setup>
import { ref, onMounted, defineProps, defineEmits } from 'vue';
import { useLayoutComponents } from '@/composables/layout.js';
import { useToast } from '@/composables/use-toast';
import axios from 'axios';

const { TextInput, SelectInput, PrimaryButton, InputError, ButtonLoading } = useLayoutComponents();
const { addToast } = useToast();

const props = defineProps({
    isActive: {
        type: Boolean,
        required: true
    },
    rfpRespond: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['close']);

const loading = ref(false);
const datasets = ref([]);

const rfpRespond = ref({
    selectedDataset: null,
    message: '',
    receipt_address: '',
    errors: {
        selectedDataset: '',
        message: '',
        receipt_address: ''
    }
});

const validateEthereumAddress = (address) => {
    const ethereumAddressRegex = /^0x[a-fA-F0-9]{40}$/;
    return ethereumAddressRegex.test(address);
};

const validateRFPForm = () => {
    let isValid = true;
    rfpRespond.value.errors = {
        selectedDataset: '',
        message: '',
        receipt_address: ''
    };

    if (!rfpRespond.value.selectedDataset) {
        rfpRespond.value.errors.selectedDataset = 'Please select a dataset';
        isValid = false;
    }

    if (!rfpRespond.value.message.trim()) {
        rfpRespond.value.errors.message = 'Please enter a message';
        isValid = false;
    }

    if (!rfpRespond.value.receipt_address.trim()) {
        rfpRespond.value.errors.receipt_address = 'Please enter a receipt address';
        isValid = false;
    } else if (!validateEthereumAddress(rfpRespond.value.receipt_address)) {
        rfpRespond.value.errors.receipt_address = 'Invalid receipt address';
        isValid = false;
    }

    return isValid;
};

const handleSubmit = async () => {
    if (!validateRFPForm()) {
        addToast('Please fill in all required fields correctly', 'error');
        return;
    }

    loading.value = true;
    
    try {
        const response = await axios.post('/api/rfp-requests', {
            listing_id: props.rfpRespond.id,
            dataset_id: Number(rfpRespond.value.selectedDataset),
            seller_message: rfpRespond.value.message,
            receipt_address: rfpRespond.value.receipt_address
        });
        
        if(response.data) {
            addToast('RFP response received.', 'success');
            handleClose();
            window.location.href = '/rfp-confirmation';
        } else {
            addToast('Failed to submit RFP response.', 'error');
        }
    } catch (error) {
        addToast(error.response?.data?.message || 'Error submitting RFP request', 'error');
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    rfpRespond.value.selectedDataset = null;
    rfpRespond.value.message = '';
    rfpRespond.value.receipt_address = '';
    rfpRespond.value.errors = {
        selectedDataset: '',
        message: '',
        receipt_address: ''
    };
};

const handleClose = () => {
    resetForm();
    emit('close');
};

const fetchDatasets = async () => {
    const response = await axios.get(`/api/datasets`);
    if(response.data) {
        datasets.value = response.data.map(dataset => ({
            label: dataset.name,
            value: dataset.id
        }));
    }
};

onMounted(() => {
    fetchDatasets();
});
</script>
<template>
    <div id="rfp-respond" :class="isActive ? 'fixed' : 'hidden'" tabindex="-1"
        class="z-50 overflow-hidden inset-0 m-auto flex justify-center items-center bg-echoes-background-black/50 backdrop-blur-sm">
        <div class="relative w-full h-auto max-w-lg p-4 animate-slideIn">
            <div class="relative bg-echoes-background-gray rounded-[21px] shadow-xl border border-echoes-border-light">
                <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-light">
                    <h5 class="text-xl font-semibold">RFP Response</h5>
                </div>
                <div class="p-6">
                    <div class="grid grid-cols-1 mb-4">
                        <div class="field-input-group-item-label-container">
                            <div class="field-input-group-item-label font-medium flex items-center">Dataset <p class="text-sm text-red-600 ml-0.5">*</p></div>
                            <InputError v-if="rfpRespond.errors.selectedDataset" :message="rfpRespond.errors.selectedDataset" class="label-error" />
                        </div>
                        <SelectInput v-model="rfpRespond.selectedDataset" :options="datasets" placeholder="Select a dataset" class="w-full" :error="!!rfpRespond.errors.selectedDataset" />
                    </div>
                    <div class="grid grid-cols-1 mb-4">
                        <div class="field-input-group-item-label-container">
                            <div class="field-input-group-item-label font-medium flex items-center">Message <p class="text-sm text-red-600 ml-0.5">*</p></div>
                            <InputError v-if="rfpRespond.errors.message" :message="rfpRespond.errors.message" class="label-error" />
                        </div>
                        <TextInput v-model="rfpRespond.message" placeholder="Enter your message" type="text" :isTextarea="true" :error="!!rfpRespond.errors.message" />
                    </div>
                    <div class="grid grid-cols-1 mb-4">
                        <div class="field-input-group-item-label-container">
                            <div class="field-input-group-item-label font-medium flex items-center">Receipt Address <p class="text-sm text-red-600 ml-0.5">*</p></div>
                            <InputError v-if="rfpRespond.errors.receipt_address" :message="rfpRespond.errors.receipt_address" class="label-error" />
                        </div>
                        <TextInput v-model="rfpRespond.receipt_address" placeholder="Enter your receipt address" type="text" :error="!!rfpRespond.errors.receipt_address" />
                    </div>
                </div>
                <div class="flex justify-end items-center px-6 py-4 border-t border-gray-100 dark:border-gray-700 gap-4">
                    <PrimaryButton @click="handleClose" :disabled="loading" type="secundary" class="bg-echoes-background-gray/50 text-white hover:bg-echoes-background-black/80 hover:text-white">Cancel</PrimaryButton>
                    <PrimaryButton @click="handleSubmit" :disabled="loading">
                        <ButtonLoading v-if="loading" :size="'xs'" :text="'Submitting...'" />
                        <span v-else>Submit</span>
                    </PrimaryButton>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="sass" scoped>
.field-input-group-item-label {
    @apply text-sm text-gray-200;
}

.field-input-group-item-label-container {
    @apply flex items-center mb-2 w-fit gap-2;
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.animate-slideIn {
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
</style> 